import type { TabsProps } from "antd";
import { Skeleton, Tabs } from "antd";
import { Briefcase, Hexagon, UserCircle } from "lucide-react";
import { useEffect, useState } from "react";

import Modal from "@/components/Modal";
import type { RolePermOpenParams } from "@/components/RoleDetailModal";

import { EMPLOYEE_STATUS_API_COLORS } from "../../constants";
import { BasicInfoTab, EmployeeHeaderCard, LicenseTab, WorkInfoTab } from "./detailTabs";
import { useEmployeeDetail } from "./hooks/useEmployeeDetail";

/** 员工详情 Modal props */
export interface EmployeeDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** 员工 ID，打开时拉取详情 */
  employeeId: string | null;
  onOpenRole: (params: RolePermOpenParams) => void;
}

/**
 * 员工详情 Modal：信息名片 + 三个 Tab（基础信息 / 工作信息 / 证照信息）
 */
export const EmployeeDetailModal = ({
  open,
  onClose,
  employeeId,
  onOpenRole,
}: EmployeeDetailModalProps) => {
  const [activeTab, setActiveTab] = useState("basic");
  const { employee, loading } = useEmployeeDetail(employeeId, open);

  /** 关闭时重置 Tab */
  useEffect(() => {
    if (open) return;
    setActiveTab("basic");
  }, [open]);

  const work = employee?.workInfo;
  const status = work?.employeeStatus;
  const statusLabel = work?.employeeStatusLabel ?? status ?? "—";
  const statusColor = status ? (EMPLOYEE_STATUS_API_COLORS[status] ?? "default") : "default";

  const tabItems: TabsProps["items"] = employee
    ? [
        {
          key: "basic",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <UserCircle className="w-3.5 h-3.5" />
              基础信息
            </span>
          ),
          children: <BasicInfoTab detail={employee} />,
        },
        {
          key: "work",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Briefcase className="w-3.5 h-3.5" />
              工作信息
            </span>
          ),
          children: <WorkInfoTab detail={employee} onOpenRole={onOpenRole} />,
        },
        {
          key: "license",
          label: (
            <span className="inline-flex items-center gap-1.5">
              <Hexagon className="w-3.5 h-3.5" />
              证照信息
            </span>
          ),
          children: <LicenseTab detail={employee} />,
        },
      ]
    : [];

  return (
    <Modal
      open={open}
      width={900}
      title="员工详情"
      titleIcon={<Briefcase className="w-5 h-5 text-orange-500" />}
      footer={null}
      onCancel={onClose}
      styles={{ content: { height: "80vh" } }}
    >
      {loading ? (
        <div className="p-5 space-y-4">
          <Skeleton active avatar paragraph={{ rows: 2 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ) : employee ? (
        <>
          <EmployeeHeaderCard
            detail={employee}
            statusLabel={statusLabel}
            statusColor={statusColor}
          />
          <Tabs
            activeKey={activeTab}
            items={tabItems}
            className="px-5 pb-4 pt-2"
            onChange={setActiveTab}
          />
        </>
      ) : (
        <div className="py-12 text-center text-sm text-slate-400">暂无员工详情</div>
      )}
    </Modal>
  );
};
